import { createFileRoute, Link, useNavigate, notFound } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useSuspenseQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { productBySlugQuery, specLabel } from "@/lib/products";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { formatBRL, whatsappLink } from "@/lib/format";
import { useStoreSettings } from "@/lib/settings.functions";
import {
  MessageCircle,
  ShoppingCart,
  Minus,
  Plus,
  ChevronRight,
  Star,
  Heart,
  Loader2,
} from "lucide-react";
import {
  productReviewsQuery,
  myReviewQuery,
  submitReview,
  deleteMyReview,
  getProductReviews,
  type ProductReview,
} from "@/lib/reviews.functions";
import { addToWishlist, removeFromWishlist, myWishlistQuery } from "@/lib/wishlist.functions";
import { requestOriginQuery } from "@/lib/seo.functions";

export const Route = createFileRoute("/produto/$slug")({
  loader: async ({ context, params }) => {
    const product = await context.queryClient.ensureQueryData(productBySlugQuery(params.slug));
    if (!product) throw notFound();
    const origin = await context.queryClient.ensureQueryData(requestOriginQuery());
    return { ...product, origin };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Produto não encontrado — Loja EG Elétrica" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const canonicalUrl = loaderData.origin
      ? `${loaderData.origin}/produto/${loaderData.slug}`
      : undefined;
    return {
      meta: [
        { title: `${loaderData.name} — Loja EG Elétrica` },
        { name: "description", content: loaderData.short_description ?? loaderData.name },
        { property: "og:title", content: loaderData.name },
        { property: "og:description", content: loaderData.short_description ?? "" },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(canonicalUrl ? [{ property: "og:url", content: canonicalUrl }] : []),
        ...(loaderData.images[0]
          ? [
              { property: "og:image", content: loaderData.images[0] },
              { name: "twitter:image", content: loaderData.images[0] },
            ]
          : []),
      ],
      links: canonicalUrl ? [{ rel: "canonical", href: canonicalUrl }] : [],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: loaderData.name,
            description: loaderData.short_description ?? loaderData.description,
            image: loaderData.images,
            sku: loaderData.sku,
            offers: loaderData.price
              ? {
                  "@type": "Offer",
                  price: loaderData.price,
                  priceCurrency: "BRL",
                  availability:
                    loaderData.stock_quantity > 0
                      ? "https://schema.org/InStock"
                      : "https://schema.org/OutOfStock",
                }
              : undefined,
          }),
        },
      ],
    };
  },
  component: ProdutoPage,
});

function ProdutoPage() {
  const { slug } = Route.useParams();
  const { data: product } = useSuspenseQuery(productBySlugQuery(slug));
  const [qty, setQty] = useState(1);
  const [expandedDesc, setExpandedDesc] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const addItem = useCart((s) => s.addItem);
  const openDrawer = useCart((s) => s.openDrawer);
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const settings = useStoreSettings();

  const { data: wishlist } = useQuery({ ...myWishlistQuery(), enabled: !!user });
  const isWishlisted = !!wishlist?.some((p) => p.id === product?.id);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const toggleWishlist = async () => {
    if (!product) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        await removeFromWishlist({ data: product.id });
      } else {
        await addToWishlist({ data: product.id });
      }
      queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
    } finally {
      setWishlistLoading(false);
    }
  };

  if (!product) return null;

  const specs = Object.entries(product.technical_specs ?? {});
  const longDesc = (product.description ?? "").length > 260;
  const displayedDesc =
    !expandedDesc && longDesc
      ? (product.description ?? "").slice(0, 260) + "…"
      : product.description;

  const outOfStock = !product.is_custom_quote && product.stock_quantity <= 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      <nav
        aria-label="Trilha de navegação"
        className="flex items-center gap-1 text-xs text-muted-foreground mb-4"
      >
        <Link to="/" className="hover:text-eg-dark">
          Loja
        </Link>
        <ChevronRight className="h-3 w-3" />
        {product.category && (
          <>
            <span>{product.category.name}</span>
            <ChevronRight className="h-3 w-3" />
          </>
        )}
        <span className="text-eg-dark truncate">{product.name}</span>
      </nav>

      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <div className="aspect-square overflow-hidden rounded-lg bg-white border border-border">
            {product.images[activeImg] && (
              <img
                src={product.images[activeImg]}
                alt={product.name}
                fetchPriority="high"
                className="h-full w-full object-cover"
              />
            )}
          </div>
          {product.images.length > 1 && (
            <div className="mt-3 flex gap-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  className={`h-16 w-16 overflow-hidden rounded border-2 ${
                    i === activeImg ? "border-eg-gold" : "border-transparent"
                  }`}
                >
                  <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-eg-dark">
              {product.name}
            </h1>
            <button
              type="button"
              onClick={toggleWishlist}
              disabled={wishlistLoading}
              aria-label={isWishlisted ? "Remover dos favoritos" : "Adicionar aos favoritos"}
              className="shrink-0 rounded-full border border-border p-2 hover:border-red-400 disabled:opacity-50 transition-colors"
            >
              {wishlistLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Heart
                  className={`h-5 w-5 ${isWishlisted ? "fill-red-500 text-red-500" : "text-muted-foreground"}`}
                />
              )}
            </button>
          </div>
          {product.short_description && (
            <p className="mt-2 text-base text-muted-foreground">{product.short_description}</p>
          )}

          {!product.is_custom_quote && (
            <div className="mt-4">
              {product.compare_at_price &&
                product.price &&
                product.compare_at_price > product.price && (
                  <p className="text-sm text-muted-foreground line-through">
                    {formatBRL(product.compare_at_price)}
                  </p>
                )}
              <p className="text-3xl font-bold text-eg-goldtext">{formatBRL(product.price)}</p>
              <p
                className={`mt-1 text-sm font-medium ${outOfStock ? "text-red-600" : "text-green-700"}`}
              >
                {outOfStock ? "Esgotado" : `Em estoque (${product.stock_quantity} unidades)`}
              </p>
            </div>
          )}

          {specs.length > 0 && (
            <div className="mt-6 rounded-lg border border-border bg-white p-4">
              <h2 className="font-display text-sm font-bold text-eg-dark uppercase tracking-wide">
                Especificações Técnicas
              </h2>
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {specs.map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between gap-2 border-b border-border/60 py-1"
                  >
                    <dt className="text-muted-foreground">{specLabel(k)}</dt>
                    <dd className="font-medium text-eg-dark text-right">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {product.description && (
            <div className="mt-6">
              <h2 className="font-display text-sm font-bold text-eg-dark uppercase tracking-wide">
                Descrição
              </h2>
              <p className="mt-2 text-sm text-foreground whitespace-pre-line">{displayedDesc}</p>
              {longDesc && (
                <button
                  type="button"
                  onClick={() => setExpandedDesc(!expandedDesc)}
                  className="mt-1 text-sm font-medium text-eg-blue hover:underline"
                >
                  {expandedDesc ? "Ver menos" : "Ver mais"}
                </button>
              )}
            </div>
          )}

          <div className="mt-6 space-y-3">
            {product.is_custom_quote ? (
              <>
                <a
                  href={whatsappLink(
                    settings.whatsapp_number,
                    `Olá! Tenho interesse em um painel personalizado: ${product.name}`,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-3 font-semibold text-white hover:bg-green-700 transition-colors"
                >
                  <MessageCircle className="h-5 w-5" />
                  Solicitar Orçamento pelo WhatsApp
                </a>
                <p className="text-center text-xs text-muted-foreground">
                  Nosso técnico responde em até 2 horas úteis
                </p>
              </>
            ) : (
              <>
                {!outOfStock && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Quantidade:</span>
                    <div className="flex items-center gap-1 rounded border border-border">
                      <button
                        type="button"
                        className="p-2 hover:bg-muted"
                        onClick={() => setQty(Math.max(1, qty - 1))}
                        aria-label="Diminuir"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="min-w-[2.5rem] text-center font-medium">{qty}</span>
                      <button
                        type="button"
                        className="p-2 hover:bg-muted"
                        onClick={() => setQty(Math.min(product.stock_quantity, qty + 1))}
                        aria-label="Aumentar"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  disabled={outOfStock}
                  onClick={() => {
                    addItem(product, qty);
                    openDrawer();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-eg-dark px-4 py-3 font-semibold text-white hover:bg-eg-gold hover:text-eg-dark disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {outOfStock ? "Esgotado" : "Adicionar ao Carrinho"}
                </button>
                <button
                  type="button"
                  disabled={outOfStock}
                  onClick={() => {
                    addItem(product, qty);
                    navigate({ to: "/checkout" });
                  }}
                  className="w-full rounded-md border-2 border-eg-gold bg-eg-gold px-4 py-3 font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Comprar Agora
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <ReviewsSection productId={product.id} loggedIn={!!user} />
    </div>
  );
}

function StarRow({ rating, size = "h-4 w-4" }: { rating: number; size?: string }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= rating ? "fill-eg-gold text-eg-gold" : "text-muted-foreground"}`}
        />
      ))}
    </div>
  );
}

function ReviewsSection({ productId, loggedIn }: { productId: string; loggedIn: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: summary } = useQuery(productReviewsQuery(productId));
  const { data: myReview } = useQuery({ ...myReviewQuery(productId), enabled: loggedIn });

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [editing, setEditing] = useState(false);

  const [extraReviews, setExtraReviews] = useState<ProductReview[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Reseta a paginação sempre que a primeira página muda (troca de produto
  // ou invalidação após enviar/excluir uma avaliação) — sem isso, avaliações
  // já carregadas via "carregar mais" ficariam duplicadas ou desatualizadas.
  useEffect(() => {
    setExtraReviews([]);
    setHasMore(summary?.hasMore ?? false);
  }, [summary]);

  const allReviews = [...(summary?.reviews ?? []), ...extraReviews];

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const next = await getProductReviews({
        data: { productId, offset: allReviews.length },
      });
      setExtraReviews((prev) => [...prev, ...next.reviews]);
      setHasMore(next.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  const startEditing = () => {
    setRating(myReview?.rating ?? 0);
    setComment(myReview?.comment ?? "");
    setEditing(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      setFormError("Escolha uma nota de 1 a 5 estrelas.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      await submitReview({ data: { productId, rating, comment: comment || undefined } });
      queryClient.invalidateQueries({ queryKey: ["product-reviews", productId] });
      queryClient.invalidateQueries({ queryKey: ["my-review", productId] });
      setEditing(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Não foi possível enviar sua avaliação.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await deleteMyReview({ data: productId });
      queryClient.invalidateQueries({ queryKey: ["product-reviews", productId] });
      queryClient.invalidateQueries({ queryKey: ["my-review", productId] });
      setEditing(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 border-t border-border mt-10">
      <h2 className="font-display text-xl font-bold text-eg-dark mb-4">Avaliações</h2>

      {summary && summary.count > 0 ? (
        <div className="mb-6 flex items-center gap-3">
          <StarRow rating={Math.round(summary.averageRating ?? 0)} size="h-5 w-5" />
          <span className="text-sm font-semibold text-eg-dark">{summary.averageRating}</span>
          <span className="text-sm text-muted-foreground">
            ({summary.count} avaliaç{summary.count === 1 ? "ão" : "ões"})
          </span>
        </div>
      ) : (
        <p className="mb-6 text-sm text-muted-foreground">Este produto ainda não tem avaliações.</p>
      )}

      <div className="mb-8 max-w-lg">
        {!loggedIn ? (
          <button
            type="button"
            onClick={() => navigate({ to: "/login" })}
            className="text-sm font-medium text-eg-goldtext hover:underline"
          >
            Entrar para avaliar este produto
          </button>
        ) : myReview && !editing ? (
          <div className="rounded-lg border border-border bg-white p-4">
            <p className="text-sm font-medium text-eg-dark mb-1">Sua avaliação</p>
            <StarRow rating={myReview.rating} />
            {myReview.comment && <p className="mt-2 text-sm text-foreground">{myReview.comment}</p>}
            <div className="mt-3 flex gap-3 text-xs">
              <button
                type="button"
                onClick={startEditing}
                className="font-medium text-eg-goldtext hover:underline"
              >
                Editar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className="font-medium text-red-600 hover:underline disabled:opacity-50"
              >
                Excluir
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-border bg-white p-4 space-y-3"
          >
            <p className="text-sm font-medium text-eg-dark">
              {myReview ? "Editar sua avaliação" : "Deixe sua avaliação"}
            </p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  aria-label={`${n} estrelas`}
                >
                  <Star
                    className={`h-6 w-6 ${n <= rating ? "fill-eg-gold text-eg-gold" : "text-muted-foreground"}`}
                  />
                </button>
              ))}
            </div>
            <textarea
              className="input"
              rows={3}
              placeholder="Conte como foi sua experiência (opcional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            {formError && <p className="text-xs text-red-600">{formError}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="rounded-md bg-eg-gold px-4 py-2 text-sm font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-60 transition-colors"
              >
                {submitting ? "Enviando..." : "Enviar avaliação"}
              </button>
              {myReview && (
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium text-eg-dark hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Só é possível avaliar produtos que você já comprou.
            </p>
          </form>
        )}
      </div>

      {allReviews.length > 0 && (
        <ul className="max-w-2xl space-y-4">
          {allReviews.map((review) => (
            <li key={review.id} className="border-b border-border pb-4">
              <div className="flex items-center gap-2">
                <StarRow rating={review.rating} />
                <span className="text-sm font-medium text-eg-dark">{review.reviewer_name}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(review.created_at).toLocaleDateString("pt-BR")}
                </span>
              </div>
              {review.comment && <p className="mt-1 text-sm text-foreground">{review.comment}</p>}
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="mt-4 max-w-2xl">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-eg-dark transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            Carregar mais avaliações
          </button>
        </div>
      )}

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </section>
  );
}
